import type { Circuit } from '../types/track.js';
import { cellCoordKey } from './circuitEdit.js';
import {
  canPlaceAmbientCell,
  decorCellKeys,
  isDecorCell,
  isInfrastructureCell,
  isPropCell,
  isWithinGrid,
  listInfrastructureCells,
  type GridCoord,
} from './circuitOccupancy.js';
import { clearAmbientFillAt } from './circuitDecorSurfaceEdit.js';

export type { GridCoord as CircuitDecorCell };

const ORTHO_DELTAS = [
  { x: 1, y: 0 },
  { x: -1, y: 0 },
  { x: 0, y: 1 },
  { x: 0, y: -1 },
];

export { decorCellKeys, isDecorCell, listInfrastructureCells };

export function canPlaceDecorCell(circuit: Circuit, x: number, y: number): boolean {
  if (!canPlaceAmbientCell(circuit, x, y)) return false;
  if (isPropCell(circuit, x, y)) return false;
  return true;
}

export function getDecorPlaceableCells(circuit: Circuit): GridCoord[] {
  const keys = new Set<string>();
  const result: GridCoord[] = [];
  for (let y = 0; y < circuit.height; y++) {
    for (let x = 0; x < circuit.width; x++) {
      if (!canPlaceDecorCell(circuit, x, y) || isDecorCell(circuit, x, y)) continue;
      const key = cellCoordKey(x, y);
      if (keys.has(key)) continue;
      keys.add(key);
      result.push({ x, y });
    }
  }
  return result;
}

function withDecorCells(circuit: Circuit, decorCells: GridCoord[]): Circuit {
  return {
    ...circuit,
    decorCells: decorCells.length > 0 ? decorCells : undefined,
  };
}

export function addDecorCell(
  circuit: Circuit,
  x: number,
  y: number
): { circuit: Circuit } | { error: string } {
  if (!canPlaceDecorCell(circuit, x, y)) {
    return { error: 'Case invalide pour le décor' };
  }
  const cleared = clearAmbientFillAt(circuit, x, y);
  if (isDecorCell(cleared, x, y)) {
    return { circuit: cleared };
  }
  const next = [...(cleared.decorCells ?? []), { x, y }];
  return { circuit: withDecorCells(cleared, next) };
}

export function removeDecorCell(circuit: Circuit, x: number, y: number): Circuit {
  if (!isDecorCell(circuit, x, y)) return circuit;
  const key = cellCoordKey(x, y);
  const next = (circuit.decorCells ?? []).filter((cell) => cellCoordKey(cell.x, cell.y) !== key);
  return withDecorCells(circuit, next);
}

export function toggleDecorCell(
  circuit: Circuit,
  x: number,
  y: number
): { circuit: Circuit } | { error: string } {
  if (isDecorCell(circuit, x, y)) {
    return { circuit: removeDecorCell(circuit, x, y) };
  }
  return addDecorCell(circuit, x, y);
}

export function clearDecorCells(circuit: Circuit): Circuit {
  if (!circuit.decorCells?.length) return circuit;
  return { ...circuit, decorCells: undefined };
}

/** Remplit les cases vides autour du circuit sur `rings` anneau(x). */
export function fillDecorAroundCircuit(circuit: Circuit, rings = 1): Circuit {
  if (rings < 1) return circuit;
  let next = circuit;
  const keys = decorCellKeys(next);

  for (let ring = 0; ring < rings; ring++) {
    const seeds = [...listInfrastructureCells(next), ...(next.decorCells ?? [])];
    const additions: GridCoord[] = [];

    for (const seed of seeds) {
      for (const delta of ORTHO_DELTAS) {
        const x = seed.x + delta.x;
        const y = seed.y + delta.y;
        const key = cellCoordKey(x, y);
        if (!isWithinGrid(next, x, y)) continue;
        if (isInfrastructureCell(next, x, y)) continue;
        if (isPropCell(next, x, y)) continue;
        if (keys.has(key)) continue;
        keys.add(key);
        additions.push({ x, y });
      }
    }

    if (additions.length === 0) break;
    next = withDecorCells(next, [...(next.decorCells ?? []), ...additions]);
  }

  return next;
}

export function pruneDecorCells(circuit: Circuit): Circuit {
  const decorCells = circuit.decorCells;
  if (!decorCells?.length) return circuit;
  const next = decorCells.filter(
    (cell) =>
      isWithinGrid(circuit, cell.x, cell.y) &&
      !isInfrastructureCell(circuit, cell.x, cell.y) &&
      !isPropCell(circuit, cell.x, cell.y)
  );
  return withDecorCells(circuit, next);
}
